(function () {
  'use strict';

  function expandYear(value) {
    const text = String(value || '');
    if (!/^\d{2}(?:\d{2})?$/.test(text)) return null;
    if (text.length === 4) return Number(text);
    const year = Number(text);
    return year <= 30 ? 2000 + year : 1900 + year;
  }

  function createValidResult(monthText, dayText, yearText) {
    const month = Number(monthText);
    const day = Number(dayText);
    const year = expandYear(yearText);
    if (!year || month < 1 || month > 12 || day < 1) return null;

    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return {
      status: 'valid',
      date,
      display: `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`,
    };
  }

  function parseCompactDate(digits) {
    const candidatesByLength = {
      4: [[1, 1, 2]],
      5: [[2, 1, 2], [1, 2, 2]],
      6: [[2, 2, 2], [1, 1, 4]],
      7: [[2, 1, 4], [1, 2, 4]],
      8: [[2, 2, 4]],
    };
    const candidates = candidatesByLength[digits.length] || [];

    for (const [monthLength, dayLength, yearLength] of candidates) {
      const monthEnd = monthLength;
      const dayEnd = monthEnd + dayLength;
      const result = createValidResult(
        digits.slice(0, monthEnd),
        digits.slice(monthEnd, dayEnd),
        digits.slice(dayEnd, dayEnd + yearLength)
      );
      if (result) return result;
    }
    return null;
  }

  function normalizeDateInput(value) {
    const text = String(value || '').trim();
    if (!text) return {status: 'empty', display: ''};
    if (!/^[\d/]+$/.test(text)) return {status: 'invalid', display: text};

    if (text.includes('/')) {
      const parts = text.split('/');
      if (parts.length < 3) return {status: 'incomplete', display: text};
      if (
        parts.length !== 3 ||
        !/^\d{1,2}$/.test(parts[0]) ||
        !/^\d{1,2}$/.test(parts[1]) ||
        !/^\d{2}(?:\d{2})?$/.test(parts[2])
      ) {
        return {status: 'invalid', display: text};
      }
      return createValidResult(parts[0], parts[1], parts[2]) || {status: 'invalid', display: text};
    }

    if (text.length < 4) return {status: 'incomplete', display: text};
    if (text.length > 8) return {status: 'invalid', display: text};
    return parseCompactDate(text) || {status: 'invalid', display: text};
  }

  function buildFixedDateMask(value) {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }

  function attachDateAutoFormat(input, options = {}) {
    if (!input) return;
    input._dateAutoFormatOptions = options;
    if (input._dateAutoFormatAttached) return;
    input._dateAutoFormatAttached = true;

    let manualSeparatorMode = false;
    let compactSourceDigits = '';
    let lastRenderedValue = '';
    let userTypedValue = null;

    input.addEventListener('keydown', function (event) {
      if (event.ctrlKey || event.metaKey) return;
      const pass = [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End',
      ];
      if (pass.includes(event.key)) return;
      if (event.key === '/') {
        manualSeparatorMode = true;
        return;
      }
      if (!/^\d$/.test(event.key)) event.preventDefault();
    });

    input.addEventListener('focus', function () {
      manualSeparatorMode = this.value.includes('/');
      compactSourceDigits = '';
      userTypedValue = null;
      lastRenderedValue = this.value;
    });

    input.addEventListener('input', function (event) {
      const rawValue = this.value.replace(/[^\d/]/g, '').slice(0, 10);
      const previousSlashCount = (lastRenderedValue.match(/\//g) || []).length;
      const currentSlashCount = (rawValue.match(/\//g) || []).length;

      if (currentSlashCount > previousSlashCount) {
        manualSeparatorMode = true;
      } else if (currentSlashCount === 0) {
        manualSeparatorMode = false;
      }

      if (manualSeparatorMode) {
        this.value = rawValue;
      } else {
        compactSourceDigits = rawValue.replace(/\D/g, '').slice(0, 8);
        this.value = buildFixedDateMask(compactSourceDigits);
      }

      lastRenderedValue = this.value;
      if (event.isTrusted) userTypedValue = this.value;
    });

    input.addEventListener('blur', function () {
      const typedValue = userTypedValue?.trim() || '';
      const typedResult = normalizeDateInput(typedValue);
      const preserveInvalidFullYear =
        /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(typedValue) &&
        typedResult.status !== 'valid';
      const displayValue = preserveInvalidFullYear ? typedValue : this.value.trim();
      const source = manualSeparatorMode
        ? displayValue
        : compactSourceDigits || displayValue;
      const result = preserveInvalidFullYear ? typedResult : normalizeDateInput(source);
      if (result.status === 'valid') this.value = result.display;
      else this.value = displayValue;
      lastRenderedValue = this.value;
      const committedResult =
        result.status === 'valid' ? result : {...result, display: this.value};
      this._dateAutoFormatOptions?.onCommit?.(committedResult, this);
      manualSeparatorMode = this.value.includes('/');
      compactSourceDigits = '';
      userTypedValue = null;
    });

    input.addEventListener('paste', function (event) {
      event.preventDefault();
      const pasted = (event.clipboardData || window.clipboardData).getData('text').trim();
      if (!/^[\d/]+$/.test(pasted)) return;
      const cleaned = pasted.slice(0, 10);
      const result = normalizeDateInput(cleaned);
      this.value = result.status === 'valid' ? result.display : cleaned;
      lastRenderedValue = this.value;
      userTypedValue = this.value;
      manualSeparatorMode = cleaned.includes('/');
      compactSourceDigits = manualSeparatorMode ? '' : cleaned.replace(/\D/g, '').slice(0, 8);
      this.dispatchEvent(new Event('input', {bubbles: true}));
    });
  }

  window.rsDateInputFormatting = {normalize: normalizeDateInput};
  window.attachDateAutoFormat = attachDateAutoFormat;
})();
